import type {
  BrandInformation,
  ConnectionStatus,
  Contact,
  Message,
  Translations,
  User,
  VoiceSessionState,
} from "./types";

export interface ThreadState {
  id: number;
  messages: Message[];
  closedAt: string | null;
  rating: number | null;
  connected: boolean;
  assignedUser: User | null;
}

export interface AgentsState {
  online: User[];
  invited: User | null;
  typing: boolean;
}

export interface VoiceState {
  state: VoiceSessionState;
  transcript: string;
}

export interface WidgetConfig {
  voiceModeAvailable: boolean;
  /** Whether visitors may attach files (LAS-3469 / LAS-4711). */
  attachmentsEnabled: boolean;
  conversationStarters: [string, string][];
  averageResponseTime: number;
  theme: "default" | "dna";
}

export interface UiState {
  /**
   * True while the default UI's chat panel is mounted and interactive — its
   * effects have flushed, so the `nav:*` listeners are attached and commands
   * like `showPage("history")` land. This is the readiness signal behind
   * `core.open()`'s promise (LAS-2166): `widget:open` marks intent, the panel
   * mounts on the next React commit, and the UI acks back through
   * `_setPanelInteractive`. Always false in headless mode (no default UI ever
   * mounts) — don't gate headless logic on it.
   */
  panelInteractive: boolean;
}

export interface LastBotState {
  connectionStatus: ConnectionStatus;
  thread: ThreadState | null;
  /**
   * The currently identified visitor. Populated after a successful login flow
   * (loginWithEmail → submitLoginCode) or when the server returns a Contact
   * with a new thread. Distinct from `agents.online` (which lists support staff).
   */
  contact: Contact | null;
  agents: AgentsState;
  voice: VoiceState;
  brand: BrandInformation | null;
  translations: Translations;
  widget: WidgetConfig | null;
  ui: UiState;
}

export const initialState = (): LastBotState => ({
  connectionStatus: "connecting",
  thread: null,
  contact: null,
  agents: {
    online: [],
    invited: null,
    typing: false,
  },
  voice: {
    state: "idle",
    transcript: "",
  },
  brand: null,
  translations: {},
  widget: null,
  ui: {
    panelInteractive: false,
  },
});
